/**
 * Session processing + provisional speaker resolution hooks.
 *
 * Phase 3f. Wraps the tauri-plugin-voice Rust commands that run the
 * full audio → turns + interactions + provisionals pipeline and let
 * the user link unrecognized clusters to contacts.
 *
 * Kept intentionally thin — these hooks are stateless wrappers around
 * the invoke calls. Caller owns loading/error state so complex flows
 * (like a post-session modal juggling multiple provisionals) can
 * manage their own orchestration.
 */
import { useCallback } from "react";
import { invoke } from "@tauri-apps/api/core";

import type {
  EnrollmentQualityCheck,
  ProcessedSession,
  ProcessedInteraction,
  ProcessedTurn,
} from "@/types/voice";

const isNative =
  typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

interface ProcessSessionInput {
  sessionId: string;
  audioPath: string;
  startedAt: string;
  endedAt: string | null;
  durationSeconds: number;
}

/**
 * Run transcribe + diarize + frame-classify + persist for a session
 * that has already been recorded. Returns the full processed output
 * including provisional speakers the UI needs to prompt about.
 */
export async function processSession(
  input: ProcessSessionInput
): Promise<ProcessedSession> {
  return invoke<ProcessedSession>("plugin:voice|process_session", {
    sessionId: input.sessionId,
    audioPath: input.audioPath,
    startedAt: input.startedAt,
    endedAt: input.endedAt,
    durationSeconds: input.durationSeconds,
  });
}

/**
 * Phase B.2 — every per-person signal emitted by a given session.
 * Used by SessionDetail's "What we learned about each person" block.
 * Signals stay in the store even after the session is viewed; they
 * accumulate across sessions to feed the B.3 contact portrait.
 */
export async function listSignalsForSession(
  sessionId: string
): Promise<import("@/types/voice").ContactSignal[]> {
  if (!isNative) return [];
  return invoke<import("@/types/voice").ContactSignal[]>(
    "plugin:voice|list_signals_for_session",
    { sessionId }
  );
}

/**
 * Phase B.2 — all signals attributed to a specific contact across
 * every session. Powers the Phase B.3 accumulated portrait view.
 */
export async function listSignalsForContact(
  contactId: string
): Promise<import("@/types/voice").ContactSignal[]> {
  if (!isNative) return [];
  return invoke<import("@/types/voice").ContactSignal[]>(
    "plugin:voice|list_signals_for_contact",
    { contactId }
  );
}

/**
 * Phase B.5 — every signal across every contact. Drives topic search
 * on the main search bar: fetch once when search opens, then match
 * queries client-side against signal values so users can search
 * "climbing" and find everyone tied to that topic.
 */
export async function listAllSignals(): Promise<
  import("@/types/voice").ContactSignal[]
> {
  if (!isNative) return [];
  return invoke<import("@/types/voice").ContactSignal[]>(
    "plugin:voice|list_all_signals"
  );
}

/**
 * Link a provisional cluster to a contact — creates the contact's
 * voiceprint, assigns the unlabeled turns to them, writes the
 * interaction row, removes the provisional entry + its audio snippet.
 */
export async function resolveProvisionalSpeaker(
  provisionalId: string,
  contactId: string
): Promise<ProcessedInteraction> {
  return invoke<ProcessedInteraction>(
    "plugin:voice|resolve_provisional_speaker",
    { provisionalId, contactId }
  );
}

/**
 * Phase 3n — user tapped "This was me" on a provisional cluster the
 * matcher missed as user_self. Assigns the cluster's turns to
 * user_self and folds the centroid into the user_self voiceprint via
 * the same rolling 5-sample mechanism contacts use. No interaction
 * row is created — the user does not have interactions with
 * themselves. Safe-by-construction: this is the only path that adapts
 * user_self, so auto-mismatch can never silently corrupt the anchor.
 */
export async function resolveProvisionalAsSelf(
  provisionalId: string
): Promise<void> {
  await invoke("plugin:voice|resolve_provisional_speaker_as_self", {
    provisionalId,
  });
}

/**
 * Phase 3r — pre-enrollment quality check. Called before a resolve so
 * the UI can prompt "sounds like [existing contact] — create anyway?"
 * when the provisional cluster's centroid is suspiciously close to a
 * voiceprint we already know about. Never throws user-facing errors:
 * on the native side any internal problem returns `isClean: true` so
 * a gate malfunction never blocks a legitimate enrollment.
 *
 * `contactId` is optional. Pass it when the user is explicitly picking
 * an already-known contact (we exclude that contact from the
 * similarity scan so we don't warn "sounds like themselves"); omit
 * it when the user chose "Someone new" and we want a library-wide
 * scan.
 */
export async function checkEnrollmentQuality(
  provisionalId: string,
  contactId: string | null
): Promise<EnrollmentQualityCheck> {
  if (!isNative) {
    return {
      isClean: true,
      warningCode: null,
      similarVoiceprintId: null,
      similarContactId: null,
      similarCosine: 0,
      internalMeanCosine: 1.0,
    };
  }
  return invoke<EnrollmentQualityCheck>(
    "plugin:voice|check_enrollment_quality",
    { provisionalId, contactId }
  );
}

/**
 * Mark a provisional cluster as not-a-person (music, TV, etc.) —
 * drops the row and deletes its audio snippet. Turns stay in the DB
 * with no contact assignment so they don't show up on any contact
 * card.
 */
export async function rejectProvisionalSpeaker(
  provisionalId: string
): Promise<void> {
  await invoke("plugin:voice|reject_provisional_speaker", { provisionalId });
}

/**
 * Every interaction for a given contact, newest first. Drives the
 * Interactions tab on the contact detail screen.
 */
export async function listInteractionsForContact(
  contactId: string
): Promise<ProcessedInteraction[]> {
  if (!isNative) return [];
  return invoke<ProcessedInteraction[]>(
    "plugin:voice|list_interactions_for_contact",
    { contactId }
  );
}

/**
 * Cross-contact feed of recent interactions for the Me tab. Each row
 * carries location context via JOIN on the parent session, so the
 * detail view can render a map pin without a second round trip.
 */
export async function listRecentInteractions(
  limit = 30
): Promise<ProcessedInteraction[]> {
  if (!isNative) return [];
  return invoke<ProcessedInteraction[]>(
    "plugin:voice|list_recent_interactions",
    { limit }
  );
}

/**
 * Every turn in a session, ordered by start time. Drives the
 * expandable transcript block on InteractionDetail. Each turn carries
 * its contactId (user_self / a contact identifier / null for
 * unresolved clusters) so the UI can label speakers accordingly.
 */
export async function listTurnsForSession(
  sessionId: string
): Promise<ProcessedTurn[]> {
  if (!isNative) return [];
  return invoke<ProcessedTurn[]>("plugin:voice|list_turns_for_session", {
    sessionId,
  });
}

/**
 * Wipe a contact's voiceprint (and samples) without touching their
 * transcripts or interaction history. "Forgetting a voice" is not
 * forgetting the relationship — the user can re-enroll them later
 * by resolving a future provisional cluster.
 */
export async function forgetContactVoiceprint(
  contactId: string
): Promise<void> {
  await invoke("plugin:voice|forget_contact_voiceprint", { contactId });
}

/**
 * Convenience hook exposing the three most-used actions as stable
 * callbacks. Consumer components that care about loading/error
 * state should manage it themselves — these callbacks are fire-
 * and-await.
 */
export function useSessionProcessor() {
  const process = useCallback(processSession, []);
  const resolve = useCallback(resolveProvisionalSpeaker, []);
  const resolveAsSelf = useCallback(resolveProvisionalAsSelf, []);
  const reject = useCallback(rejectProvisionalSpeaker, []);
  const listInteractions = useCallback(listInteractionsForContact, []);
  const forget = useCallback(forgetContactVoiceprint, []);

  return { process, resolve, resolveAsSelf, reject, listInteractions, forget };
}
